import { ApolloError } from "@apollo/client";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Heading,
  Input,
} from "@chakra-ui/react";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Alert, Button, Link } from "../../../components";
import DisclaimerInputs, {
  DisclaimerFormData,
} from "../../../components/DisclaimerInputs/DisclaimerInputs";
import NotetakerDisplayImageSelector from "../../../components/NotetakerDisplayImage/NotetakerDisplayImageSelector";
import {
  CallType,
  Maybe,
  NotetakerRecorderDisclaimerType,
} from "../../graphql";

export interface FormData extends DisclaimerFormData {
  enabled: boolean;
  virtualOnsiteEnabled: boolean;
  displayName?: Maybe<string>;
  googleMeetSendCancelRecordingChatMessage: boolean;
}

interface GoogleMeetSettingsFormProps {
  organizationName: string;
  googleMeetEnabled: boolean;
  googleMeetSendCancelRecordingChatMessage: boolean;
  googleMeetDisplayImageBucketPath?: Maybe<string>;
  googleMeetRecordingDisclaimer?: Maybe<string>;
  googleMeetRecordingDisclaimerType: NotetakerRecorderDisclaimerType;
  defaultDisclaimer: string;
  defaultDisplayName?: Maybe<string>;
  virtualOnsiteEnabled: boolean;
  showVirtualOnsiteSetting: boolean;
  error: ApolloError | undefined;
  isLoading: boolean;
  onSubmit: (formData: FormData) => Promise<void>;
}

const GoogleMeetSettingsForm: React.FC<GoogleMeetSettingsFormProps> = ({
  organizationName,
  googleMeetEnabled,
  googleMeetSendCancelRecordingChatMessage,
  googleMeetDisplayImageBucketPath,
  googleMeetRecordingDisclaimer,
  googleMeetRecordingDisclaimerType,
  defaultDisclaimer,
  defaultDisplayName,
  virtualOnsiteEnabled,
  showVirtualOnsiteSetting,
  error,
  isLoading,
  onSubmit,
}) => {
  const formMethods = useForm<FormData>({
    defaultValues: {
      enabled: googleMeetEnabled,
      googleMeetSendCancelRecordingChatMessage,
      disclaimer: googleMeetRecordingDisclaimer ?? "",
      disclaimerType: googleMeetRecordingDisclaimerType,
      defaultDisclaimer,
      virtualOnsiteEnabled,
      displayName: defaultDisplayName,
    },
  });
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = formMethods;

  const DEFAULT_BH_DISPLAY_IMAGE = "brighthire.jpg";
  const previewImageSrc =
    googleMeetDisplayImageBucketPath ?? DEFAULT_BH_DISPLAY_IMAGE;
  const [displayImageBase64, setDisplayImageBase64] = React.useState<
    string | null
  >(null);

  return (
    <FormProvider {...formMethods}>
      <form
        autoComplete="on"
        onSubmit={handleSubmit(async (formData) => {
          await onSubmit({
            ...formData,
            ...(displayImageBase64 && { displayImage: displayImageBase64 }),
          });
          setDisplayImageBase64(null);
        })}
      >
        {error?.graphQLErrors?.map(({ message }, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Alert mb={8} key={i} status="error" description={message} />
        ))}
        {/** Primary fields */}
        <FormControl id="googleMeetEnabled" mb={8}>
          <Checkbox {...register("enabled")} defaultChecked={googleMeetEnabled}>
            Enabled
          </Checkbox>
          <FormHelperText>
            This turns on Google Meet for your organization.
          </FormHelperText>
        </FormControl>

        <FormControl
          id="displayName"
          mb={8}
          isInvalid={errors.displayName !== undefined}
        >
          <Input
            {...register("displayName")}
            placeholder={`${organizationName} Notetaker`}
            data-testid="display-name"
            maxLength={60}
          />
          <FormHelperText>
            The name that will be displayed for the Google Meet Notetaker in
            meetings.
          </FormHelperText>
          {errors.displayName !== undefined && (
            <FormErrorMessage>{errors.displayName.message}</FormErrorMessage>
          )}
        </FormControl>

        <NotetakerDisplayImageSelector
          previewImageSrc={previewImageSrc}
          onResetToDefault={() => {
            handleSubmit(async (formData) => {
              await onSubmit({
                ...formData,
                ...{
                  displayImageFilename: DEFAULT_BH_DISPLAY_IMAGE,
                },
              });
            })();
            setDisplayImageBase64(null);
          }}
          onImageGenerated={setDisplayImageBase64}
          onImageReset={() => setDisplayImageBase64(null)}
          showResetToDefault={
            !previewImageSrc.endsWith(DEFAULT_BH_DISPLAY_IMAGE)
          }
        />

        {/** Disclaimer fields */}
        <DisclaimerInputs callType={CallType.GoogleMeet} />

        <Box mb={6} hidden={!googleMeetEnabled || !showVirtualOnsiteSetting}>
          <Divider mb={6} />
          <Heading as="h4" fontSize="md" mb={6}>
            Virtual Onsite
          </Heading>

          <FormControl id="virtualOnsiteEnabled">
            <Checkbox
              {...register("virtualOnsiteEnabled")}
              defaultChecked={virtualOnsiteEnabled}
              data-testid="virtual-onsite-enabled"
            >
              Enabled
            </Checkbox>
            <FormHelperText>
              Use interviewer attendance to automatically create separate
              interview recordings of sequential scheduled interviews that share
              a meeting meeting url. This will also provide interviewers more
              control over joining, starting, stopping the different sections of
              onsite via the Interview Assistant.
            </FormHelperText>
            <FormHelperText>
              <Link
                href="http://help.brighthire.ai/en/articles/6954308-brighthire-and-virtual-onsites"
                target="_blank"
              >
                {" Learn More"}
              </Link>
            </FormHelperText>
          </FormControl>
        </Box>

        <Box mb={6} hidden={!googleMeetEnabled}>
          <Divider mb={6} />
          <Heading as="h4" fontSize="md" mb={6}>
            Other Settings
          </Heading>
          <FormControl
            id="googleMeetSendCancelRecordingChatMessage"
            mb={4}
            mt={4}
          >
            <Checkbox
              {...register("googleMeetSendCancelRecordingChatMessage")}
              defaultChecked={googleMeetSendCancelRecordingChatMessage}
            >
              Send Cancel Recording Link
            </Checkbox>
            <FormHelperText>
              When each participants joins, the Notetaker will send them a
              message which contains a link that when clicked will remove the
              Notetaker and delete the recording.
            </FormHelperText>
          </FormControl>
        </Box>

        {/** Submit form */}
        <Divider mb={6} />
        <Button
          type="submit"
          mt={4}
          isLoading={isLoading}
          data-testid="google-meet-settings-submit"
        >
          Save
        </Button>
      </form>
    </FormProvider>
  );
};

export default GoogleMeetSettingsForm;
